import { useRef } from "react";
import { Control } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
} from "@/components/ui/form";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, Plus, X } from "lucide-react";
import {
  ANGLE_OPTIONS,
  EditableImage,
  EditableVariant,
  SmartUploadFormValues,
} from "@/schema/smartupload.schema";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DRAG_TYPE } from "@/lib/smartupload-dnd";
import { cn } from "@/lib/utils";

interface SortableImageCellProps {
  img: EditableImage;
  imgIndex: number;
  vIdx: number;
  variantId: string;
  productIndex: number;
  onRemove: (vIdx: number, imgIndex: number, img: EditableImage) => void;
  control: Control<SmartUploadFormValues>;
  formIndex: number;
}

function SortableImageCell({
  img,
  imgIndex,
  vIdx,
  variantId,
  productIndex,
  onRemove,
  control,
  formIndex,
}: SortableImageCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: img.id,
    data: {
      type: DRAG_TYPE.IMAGE,
      imageId: img.id,
      productIndex,
      variantId,
      imageIndex: imgIndex,
      productUrl: img.product_url,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
      }}
      className="flex flex-col gap-1"
    >
      <div
        className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 group/img cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <img
          src={img.product_url}
          alt={img.angle}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(vIdx, imgIndex, img)}
          className="absolute top-1 right-1 z-10 rounded-full bg-black/50 p-0.5 text-white opacity-0 group-hover/img:opacity-100 hover:bg-black/80 transition-opacity"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <FormField
        control={control}
        name={`products.${formIndex}.variants.${vIdx}.images.${imgIndex}.angle`}
        render={({ field: f }) => (
          <FormItem className="space-y-0">
            <FormControl>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="w-full text-[11px] text-center text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md py-0.5 transition-colors outline-none truncate"
                  >
                    {ANGLE_OPTIONS.find((o) => o.value === f.value)?.label ?? f.value}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  style={{ width: "var(--radix-dropdown-menu-trigger-width)" }}
                >
                  {ANGLE_OPTIONS.map((opt) => (
                    <DropdownMenuItem key={opt.value} onClick={() => f.onChange(opt.value)}>
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}

interface VariantDropZoneProps {
  variant: EditableVariant;
  vIdx: number;
  productIndex: number;
  onRemove: (vIdx: number, imgIndex: number, img: EditableImage) => void;
  onAddFile: (vIdx: number, file: File) => void;
  isAdding?: boolean;
  control: Control<SmartUploadFormValues>;
  formIndex: number;
}

export function VariantDropZone({
  variant,
  vIdx,
  productIndex,
  onRemove,
  onAddFile,
  isAdding,
  control,
  formIndex,
}: VariantDropZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setNodeRef, isOver } = useDroppable({
    id: `variant-${variant.id}`,
    data: { type: DRAG_TYPE.VARIANT, productIndex, variantId: variant.id },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "grid grid-cols-5 gap-2 min-h-[72px] rounded-xl p-1 transition-colors",
        isOver && "bg-purple-50 ring-1 ring-purple-300"
      )}
    >
      <SortableContext
        items={variant.images.map((im) => im.id)}
        strategy={rectSortingStrategy}
      >
        {variant.images.map((img, imgIndex) => (
          <SortableImageCell
            key={img.id}
            img={img}
            imgIndex={imgIndex}
            vIdx={vIdx}
            variantId={variant.id}
            productIndex={productIndex}
            onRemove={onRemove}
            control={control}
            formIndex={formIndex}
          />
        ))}
      </SortableContext>

      {/* Add image — outside SortableContext so it isn't draggable */}
      <div className="flex flex-col gap-1">
        <button
          type="button"
          disabled={isAdding}
          onClick={() => fileInputRef.current?.click()}
          className="aspect-square rounded-xl border-2 border-dashed border-gray-200 hover:border-purple-300 hover:bg-purple-50 flex items-center justify-center transition-colors disabled:opacity-60"
        >
          {isAdding ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <Plus className="h-4 w-4 text-gray-400" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onAddFile(vIdx, file);
          }}
        />
      </div>
    </div>
  );
}
